import { ChangeDetectionStrategy, Component, DestroyRef, effect, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { tap } from 'rxjs';
import { NumberInputComponent, TextInputComponent } from '@examdojo/core/input';
import { SingleSelectComponent } from '@examdojo/core/select';
import type { ActionDefinition } from '../../../../model/widget-action.model';
import { PatchDisplayTargetEditorComponent, type PatchDisplayTarget } from './patch-display-target-editor.component';

interface SelectOption {
  label: string;
  value: string;
}

type TriggerMode = 'UPDATE_PARAM' | 'PATCH_DISPLAY' | 'json';

@Component({
  selector: 'dojo-trigger-editor',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TextInputComponent,
    NumberInputComponent,
    SingleSelectComponent,
    PatchDisplayTargetEditorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-1.5">
      <div class="mt-1 text-[9px] font-semibold uppercase tracking-wider text-neutral-400">Trigger</div>

      <!-- Action type selector -->
      <dojo-single-select
        label="Action"
        [formCtrl]="actionTypeCtrl"
        [options]="actionTypeOptions"
        optionLabel="label"
        optionValue="value"
        [size]="'s'"
        (selectionChange)="onActionTypeChange($event)"
      />

      <!-- UPDATE_PARAM mode -->
      @if (mode() === 'UPDATE_PARAM') {
        <div class="grid grid-cols-2 gap-1.5">
          <dojo-text-input label="Param" [formCtrl]="paramCtrl" [size]="'s'" placeholder="f.a" />
          <dojo-number-input label="Value" [formCtrl]="valueCtrl" [step]="0.1" [size]="'s'" />
        </div>
      }

      <!-- PATCH_DISPLAY mode -->
      @if (mode() === 'PATCH_DISPLAY') {
        <div class="flex flex-col gap-1.5">
          @for (target of patchTargets(); track $index) {
            <dojo-patch-display-target-editor
              [target]="target"
              [elementIds]="elementIds()"
              (targetChange)="onTargetChange($index, $event)"
              (remove)="onTargetRemove($index)"
            />
          }
          <button
            class="rounded-md border border-dashed border-neutral-200 px-2 py-1 text-[11px] text-neutral-400 hover:border-sky-300 hover:text-sky-500"
            (click)="onAddTarget()"
          >
            + Add Target
          </button>
        </div>
      }

      <!-- JSON escape hatch -->
      @if (mode() === 'json') {
        <textarea
          class="w-full rounded-md border border-neutral-200 bg-white px-2 py-1.5 font-mono text-[11px] text-neutral-700 focus:border-sky-300 focus:outline-none"
          [class.border-red-300]="jsonError()"
          rows="6"
          [value]="jsonValue()"
          (input)="onJsonInput($event)"
          (blur)="onJsonBlur()"
        ></textarea>
        @if (jsonError()) {
          <div class="text-[10px] text-red-500">{{ jsonError() }}</div>
        }
      }
    </div>
  `,
})
export class TriggerEditorComponent {
  constructor(private readonly destroyRef: DestroyRef) {
    effect(() => {
      this.trigger();
      this.syncFromTrigger();
    });

    this.paramCtrl.valueChanges
      .pipe(
        tap(() => this.emitUpdateParam()),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.valueCtrl.valueChanges
      .pipe(
        tap(() => this.emitUpdateParam()),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly trigger = input.required<ActionDefinition>();
  readonly elementIds = input<string[]>([]);
  readonly triggerChange = output<ActionDefinition>();

  readonly mode = signal<TriggerMode>('UPDATE_PARAM');
  readonly patchTargets = signal<PatchDisplayTarget[]>([]);
  readonly jsonValue = signal<string>('');
  readonly jsonError = signal<string>('');

  readonly actionTypeCtrl = new FormControl<string>('UPDATE_PARAM', { nonNullable: true });
  readonly paramCtrl = new FormControl<string>('', { nonNullable: true });
  readonly valueCtrl = new FormControl<number | null>(null);

  readonly actionTypeOptions: SelectOption[] = [
    { label: 'Update Param', value: 'UPDATE_PARAM' },
    { label: 'Patch Display', value: 'PATCH_DISPLAY' },
    { label: 'Other (JSON)', value: 'json' },
  ];

  // ═══════════════════════════════════════════════════════════════════════════
  // Action type
  // ═══════════════════════════════════════════════════════════════════════════

  onActionTypeChange(value: unknown): void {
    const newMode = value as string;
    if (newMode === 'UPDATE_PARAM' || newMode === 'PATCH_DISPLAY' || newMode === 'json') {
      this.mode.set(newMode);
      if (newMode === 'UPDATE_PARAM') {
        this.emitUpdateParam();
      } else if (newMode === 'PATCH_DISPLAY') {
        this.emitPatchDisplay();
      }
      // json mode: don't emit until user explicitly edits
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PATCH_DISPLAY
  // ═══════════════════════════════════════════════════════════════════════════

  onTargetChange(index: number, target: PatchDisplayTarget): void {
    const targets = [...this.patchTargets()];
    targets[index] = target;
    this.patchTargets.set(targets);
    this.emitPatchDisplay();
  }

  onTargetRemove(index: number): void {
    const targets = [...this.patchTargets()];
    targets.splice(index, 1);
    this.patchTargets.set(targets);
    this.emitPatchDisplay();
  }

  onAddTarget(): void {
    this.patchTargets.update((targets) => [...targets, { elementId: '', styles: {} }]);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // JSON escape hatch
  // ═══════════════════════════════════════════════════════════════════════════

  onJsonInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    this.jsonValue.set(value);
    this.jsonError.set('');
  }

  onJsonBlur(): void {
    const raw = this.jsonValue();
    if (!raw.trim()) {
      return;
    }
    try {
      const parsed = JSON.parse(raw) as ActionDefinition;
      if (!parsed.action) {
        this.jsonError.set('Missing "action" field');
        return;
      }
      this.jsonError.set('');
      this.triggerChange.emit(parsed);
    } catch {
      this.jsonError.set('Invalid JSON');
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UPDATE_PARAM
  // ═══════════════════════════════════════════════════════════════════════════

  private emitUpdateParam(): void {
    const param = this.paramCtrl.value;
    const value = this.valueCtrl.value;
    this.triggerChange.emit({
      action: 'UPDATE_PARAM',
      payload: { param, value: value ?? 0 },
    });
  }

  private emitPatchDisplay(): void {
    const payload: Record<string, unknown> = {};
    for (const target of this.patchTargets()) {
      if (!target.elementId) {
        continue;
      }
      const entry: Record<string, unknown> = { ...target.styles };
      if (target.visible === false) {
        entry['visible'] = false;
      }
      payload[target.elementId] = entry;
    }
    this.triggerChange.emit({ action: 'PATCH_DISPLAY', payload });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Sync
  // ═══════════════════════════════════════════════════════════════════════════

  private syncFromTrigger(): void {
    const t = this.trigger();
    if (!t) {
      return;
    }

    if (t.action === 'UPDATE_PARAM') {
      this.mode.set('UPDATE_PARAM');
      this.actionTypeCtrl.setValue('UPDATE_PARAM', { emitEvent: false });
      const payload = t.payload ?? {};
      this.paramCtrl.setValue((payload['param'] as string) ?? '', { emitEvent: false });
      this.valueCtrl.setValue((payload['value'] as number) ?? null, { emitEvent: false });
    } else if (t.action === 'PATCH_DISPLAY') {
      this.mode.set('PATCH_DISPLAY');
      this.actionTypeCtrl.setValue('PATCH_DISPLAY', { emitEvent: false });
      this.patchTargets.set(this.parsePatchPayload(t.payload));
    } else {
      this.mode.set('json');
      this.actionTypeCtrl.setValue('json', { emitEvent: false });
      this.jsonValue.set(JSON.stringify(t, null, 2));
      this.jsonError.set('');
    }
  }

  private parsePatchPayload(payload?: Record<string, unknown>): PatchDisplayTarget[] {
    if (!payload) {
      return [];
    }
    const targets: PatchDisplayTarget[] = [];

    for (const [elementId, value] of Object.entries(payload)) {
      if (typeof value !== 'object' || value === null) {
        continue;
      }
      const raw = value as Record<string, unknown>;
      const styles: Record<string, Record<string, unknown>> = {};
      let visible: boolean | undefined;

      for (const [key, val] of Object.entries(raw)) {
        if (key === 'visible') {
          visible = val as boolean;
        } else if (typeof val === 'object' && val !== null) {
          styles[key] = val as Record<string, unknown>;
        }
      }

      targets.push({ elementId, styles, visible: visible === false ? false : undefined });
    }

    return targets;
  }
}
